/**
 * SamplingEditDoDialog — bulk "Edit DO" grid for Quality › Sampling Data.
 * Converted 1:1 from Access `frmQualityQrySampling_EditMany_Add` (wrapper) +
 * subform `frmQualityQrySampling_EditMany` (datasheet grid over tmp_quality_sampling).
 *
 * Behaviour (Access):
 *   - Form_Load: load every eligible sample (sample_status='In Use' AND
 *     result='Accept', id DESC) into the grid. Each row carries an editable
 *     `do_no` (lookup `SELECT do_no FROM delivery_order_scs WHERE is_status=0`)
 *     and a `checked_row` tick (default 0 = unticked).
 *   - "Tick All" header checkbox toggles every row.
 *   - Only `DO No.` is editable per row (Autocomplete do_no lookup); the other
 *     columns (Sample No, Contract Ref, Lot ID, Client Lot ID, Grade, Consignee,
 *     Result) are display-only context exactly like the Access datasheet.
 *   - btnSave_Click: loops the TICKED rows (checked_row=-1) and UPDATEs
 *     sampling.do_no for each. Unticked rows are skipped.
 *
 * No selectedRow prop — Access btnEdit2 simply DoCmd.OpenForm; the dialog loads
 * its own list. Reuses the shared `QualitySampling` cache tag (Access subForm.Requery).
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, Checkbox, CircularProgress, Dialog,
  DialogContent, DialogTitle, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'

import {
  type SamplingEditDoRow,
  type SamplingEditDoUpdateItem,
  useBulkUpdateSamplingDoNoMutation,
  useGetSamplesForEditDoQuery,
} from '../../store/api/samplingEditDoApi'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'

interface Props {
  open: boolean
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

/** Per-row tick + editable do_no draft, keyed by sampling id. */
interface RowDraft {
  checked: boolean
  doNo: string
}

const SM = 'small' as const
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
const headCellSx: SxProps<Theme> = { fontWeight: 700, fontSize: '0.75rem', whiteSpace: 'nowrap', py: 0.75 }
const bodyCellSx: SxProps<Theme> = { fontSize: '0.75rem', whiteSpace: 'nowrap', py: 0.4 }
const editFieldSx: SxProps<Theme> = {
  minWidth: 160,
  '& .MuiInputBase-root': { minHeight: 30, fontSize: '0.75rem' },
  '& .MuiInputBase-input': { py: 0.4, fontSize: '0.75rem' },
}
const s = (v: unknown): string => (v == null ? '' : String(v))

export default function SamplingEditDoDialog({ open, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [drafts, setDrafts] = useState<Record<number, RowDraft>>({})

  const { data, isFetching, isError } = useGetSamplesForEditDoQuery(undefined, { skip: !open })
  const rows: SamplingEditDoRow[] = useMemo(() => data?.rows ?? [], [data])
  const doOptions: string[] = useMemo(() => data?.doOptions ?? [], [data])

  const [updateRows, { isLoading: saving }] = useBulkUpdateSamplingDoNoMutation()

  // Hydrate drafts when rows arrive. Access default checked_row = 0 (unticked);
  // do_no pre-filled from the current sample value.
  useEffect(() => {
    if (!open) return
    const next: Record<number, RowDraft> = {}
    for (const r of rows) next[r.id] = { checked: false, doNo: s(r.doNo) }
    setDrafts(next)
  }, [open, rows])

  const setRow = (id: number, patch: Partial<RowDraft>) =>
    setDrafts((p) => ({ ...p, [id]: { ...p[id], ...patch } }))

  const allChecked = rows.length > 0 && rows.every((r) => drafts[r.id]?.checked)
  const someChecked = rows.some((r) => drafts[r.id]?.checked)
  const checkedCount = rows.filter((r) => drafts[r.id]?.checked).length

  // Access tick_all — toggle every row on/off.
  const toggleAll = (checked: boolean) =>
    setDrafts((p) => {
      const next = { ...p }
      for (const r of rows) next[r.id] = { ...next[r.id], checked }
      return next
    })

  const canSave = canEdit && !saving && checkedCount > 0
  const handleClose = () => { if (!saving) onClose() }

  // Access btnSave_Click — UPDATE do_no for each ticked row.
  const handleSave = async () => {
    if (!canSave) return
    const payload: SamplingEditDoUpdateItem[] = rows
      .filter((r) => drafts[r.id]?.checked)
      .map((r) => ({ id: r.id, doNo: drafts[r.id].doNo.trim() || null }))
    try {
      await updateRows({ rows: payload }).unwrap()
      snack.success('Successfully updated')
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Update failed'))
    }
  }

  // Display-only context columns (EditMany datasheet order by Top).
  const displayCols: { key: keyof SamplingEditDoRow; label: string }[] = [
    { key: 'sampleNo', label: 'Sample No' },
    { key: 'tlcContractRef', label: 'Contract Ref' },
    { key: 'lotId', label: 'Lot ID' },
    { key: 'clientLotId', label: 'Client Lot ID' },
    { key: 'grade', label: 'Grade' },
    { key: 'consignee', label: 'Consignee' },
    { key: 'result', label: 'Result' },
  ]

  return (
    <Dialog
      open={open}
      maxWidth="xl"
      fullWidth
      onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; handleClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" sx={{ fontWeight: 700 }}>
          Edit DO
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>(Only edit ticked rows)</Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button type="button" variant="contained" color="success" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>Cancel</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>You do not have permission to edit samples.</Alert>}
        {isError && <Alert severity="error" sx={{ mb: 1.5 }}>Could not load samples.</Alert>}

        {isFetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : rows.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 3, textAlign: 'center' }}>
            No eligible samples found.
          </Typography>
        ) : (
          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={headCellSx}>
                    <Stack direction="row" alignItems="center" spacing={0.25}>
                      <Checkbox
                        size={SM}
                        checked={allChecked}
                        indeterminate={!allChecked && someChecked}
                        onChange={(e) => toggleAll(e.target.checked)}
                        disabled={!canEdit}
                      />
                      <span>Tick All</span>
                    </Stack>
                  </TableCell>
                  <TableCell sx={headCellSx}>DO No. (EDIT)</TableCell>
                  {displayCols.map((c) => (
                    <TableCell key={c.key} sx={headCellSx}>{c.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const d = drafts[r.id]
                  if (!d) return null
                  // Inject current value into options (server lookup may not include it).
                  const merged = d.doNo && !doOptions.includes(d.doNo) ? [d.doNo, ...doOptions] : doOptions
                  return (
                    <TableRow key={r.id} hover selected={d.checked}>
                      <TableCell padding="checkbox" sx={bodyCellSx}>
                        <Checkbox
                          size={SM}
                          checked={d.checked}
                          onChange={(e) => setRow(r.id, { checked: e.target.checked })}
                          disabled={!canEdit}
                        />
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Autocomplete
                          size={SM}
                          options={merged}
                          value={d.doNo || null}
                          disabled={!canEdit}
                          sx={editFieldSx}
                          onChange={(_e, v) => setRow(r.id, { doNo: v ?? '' })}
                          isOptionEqualToValue={(a, b) => a === b}
                          renderInput={(params) => <TextField {...params} placeholder="DO No." />}
                        />
                      </TableCell>
                      {displayCols.map((c) => (
                        <TableCell key={c.key} sx={bodyCellSx}>{s(r[c.key])}</TableCell>
                      ))}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  )
}
